// Admin — App Settings. Renders the current key/value rows from
// app_settings, allows editing existing values as JSON, deleting them,
// and adding new keys via <AddSettingForm>. Values are jsonb on the
// server so any JSON-serialisable payload works.
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/utils";
import { AddSettingForm } from "./AddSettingForm";

interface SettingRow {
  key: string;
  value: unknown;
  updatedBy: number | null;
  updatedAt: string;
}

export function Settings() {
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api<{ settings: SettingRow[] }>("/api/admin/settings");
      setRows(r.settings);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const upsert = async (key: string, value: unknown) => {
    await api("/api/admin/settings", {
      method: "PUT",
      json: { key, value },
    });
    await load();
  };

  const remove = async (key: string) => {
    try {
      await api(`/api/admin/settings/${encodeURIComponent(key)}`, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="bsp-page-header">
        <h1 className="bsp-page-title">Settings</h1>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)", marginBottom: 16 }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      <AddSettingForm onUpsert={upsert} onError={setError} />

      {rows === null && !error && <p>Loading settings…</p>}
      {rows && rows.length === 0 && (
        <p style={{ color: "var(--bsp-fg-muted)" }}>
          No settings configured. Add one above.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div className="bsp-card" style={{ padding: 0 }}>
          <table className="bsp-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Updated</th>
                <th style={{ width: 48 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="bsp-mono">{r.key}</td>
                  <td className="bsp-mono" style={{ fontSize: 12 }}>
                    {JSON.stringify(r.value)}
                  </td>
                  <td className="bsp-mono" style={{ fontSize: 12 }}>
                    {formatDate(r.updatedAt)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="bsp-btn bsp-btn-icon bsp-btn-ghost"
                      onClick={() => void remove(r.key)}
                      title={`Delete ${r.key}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
